import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Shield, CheckCircle, XCircle, Trash, PencilSimple, Image as ImageIcon, Certificate, ListChecks, Tree, Warning } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ModerationManager() {
  const [tab, setTab] = useState("posts");

  return (
    <div className="space-y-5" data-testid="moderation-manager">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-full bg-amber-600/20 flex items-center justify-center">
          <Shield size={20} weight="duotone" className="text-amber-400" />
        </div>
        <div>
          <h2 className="font-heading text-lg font-semibold text-white">Moderation Queue</h2>
          <p className="text-xs text-stone-400">Review community posts and certificate issuances before they go live.</p>
        </div>
      </div>

      <div className="flex gap-2 border-b border-stone-800 overflow-x-auto scrollbar-thin">
        {[
          { id: "posts", label: "Tree Posts", Icon: Tree },
          { id: "certs", label: "Certificates", Icon: Certificate },
        ].map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)}
            data-testid={`moderation-tab-${t.id}`}
            className={`flex items-center gap-1.5 px-4 py-2 text-xs font-medium border-b-2 -mb-px whitespace-nowrap transition-colors ${
              tab === t.id ? "border-amber-400 text-amber-300" : "border-transparent text-stone-500 hover:text-stone-300"
            }`}>
            <t.Icon size={14} weight="bold" /> {t.label}
          </button>
        ))}
      </div>

      {tab === "posts" && <PostsModeration />}
      {tab === "certs" && <CertsModeration />}
    </div>
  );
}

function PostsModeration() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const url = filter === "pending" ? `${API}/admin/posts/pending` : `${API}/admin/posts/all?status=${filter}`;
      const res = await axios.get(url, { withCredentials: true });
      setItems(res.data.items || []);
    } catch (e) { setItems([]); }
    setLoading(false);
  }, [filter]);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  const action = async (id, verb, body = {}) => {
    try {
      if (verb === "delete") {
        await axios.delete(`${API}/admin/posts/${id}`, { withCredentials: true });
      } else {
        await axios.put(`${API}/admin/posts/${id}/${verb}`, body, { withCredentials: true });
      }
      fetchAll();
    } catch (e) {
      alert(e.response?.data?.detail || "Failed");
    }
  };

  return (
    <div>
      <div className="flex gap-2 mb-4">
        {["pending", "approved", "rejected"].map((f) => (
          <button key={f} onClick={() => setFilter(f)}
            data-testid={`posts-filter-${f}`}
            className={`text-xs px-3 py-1 rounded-full border ${
              filter === f ? "bg-amber-500 border-amber-600 text-white" : "border-stone-700 text-stone-400"
            }`}>
            {f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>

      {loading ? <p className="text-stone-500 text-sm">Loading…</p> : items.length === 0 ? (
        <p className="text-stone-500 text-sm italic">No {filter} posts.</p>
      ) : (
        <div className="space-y-3">
          {items.map((p) => (
            <PostRow key={p.id} post={p} onAction={action} onEdit={() => setEditing(p)} />
          ))}
        </div>
      )}

      {editing && <EditModal post={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); fetchAll(); }} />}
    </div>
  );
}

function PostRow({ post, onAction, onEdit }) {
  const [reason, setReason] = useState("");
  const [showReject, setShowReject] = useState(false);
  const imgSrc = post.image_path?.startsWith("http") ? post.image_path : `${process.env.REACT_APP_BACKEND_URL}${post.image_path}`;
  return (
    <div className="bg-stone-900 border border-stone-800 rounded-xl p-4 flex flex-col sm:flex-row gap-4" data-testid={`post-row-${post.id}`}>
      <div className="w-full sm:w-32 h-32 bg-stone-800 rounded-lg overflow-hidden flex items-center justify-center shrink-0">
        {post.image_path ? (
          <img src={imgSrc} alt="" className="w-full h-full object-cover" onError={(e) => e.target.style.display = "none"} />
        ) : <ImageIcon size={24} className="text-stone-600" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 text-xs text-stone-500 mb-1">
          <span className="text-stone-300 font-medium">{post.user_name}</span>
          <span>·</span>
          <span>{new Date(post.created_at).toLocaleString()}</span>
          <span className={`ml-auto px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider ${
            post.status === "pending" ? "bg-amber-500/20 text-amber-300" :
            post.status === "approved" ? "bg-emerald-500/20 text-emerald-300" :
            "bg-rose-500/20 text-rose-300"
          }`}>{post.status}</span>
        </div>
        <p className="text-sm text-stone-200 mb-2 line-clamp-2 break-words">{post.caption || <em className="text-stone-500">No caption</em>}</p>
        <div className="flex items-center gap-3 text-xs text-stone-400 mb-3">
          <span>🌱 {post.trees_count} tree(s)</span>
          <span>📍 {post.lat?.toFixed(3)}, {post.lon?.toFixed(3)}</span>
          <span>💨 {post.co2_kg}kg CO₂/yr</span>
        </div>
        {post.rejected_reason && (
          <p className="text-xs text-rose-300 bg-rose-500/10 border border-rose-500/20 rounded-lg px-2 py-1 mb-2">
            Rejected: {post.rejected_reason}
          </p>
        )}
        {showReject ? (
          <div className="flex gap-2 mb-2">
            <input type="text" placeholder="Reason for rejection..." value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="flex-1 px-3 py-1.5 bg-stone-800 border border-stone-700 rounded-lg text-xs text-white focus:outline-none focus:border-rose-500"
              data-testid={`post-reject-reason-${post.id}`} />
            <button onClick={() => { onAction(post.id, "reject", { reason }); setShowReject(false); }}
              className="text-xs bg-rose-600 hover:bg-rose-500 text-white px-3 py-1.5 rounded-lg"
              data-testid={`post-reject-confirm-${post.id}`}>Confirm</button>
            <button onClick={() => setShowReject(false)} className="text-xs text-stone-400">Cancel</button>
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {post.status === "pending" && (
              <>
                <button onClick={() => onAction(post.id, "approve")}
                  data-testid={`post-approve-${post.id}`}
                  className="text-xs bg-emerald-600 hover:bg-emerald-500 text-white px-3 py-1.5 rounded-lg flex items-center gap-1">
                  <CheckCircle size={12} weight="bold" /> Approve
                </button>
                <button onClick={() => setShowReject(true)}
                  data-testid={`post-reject-${post.id}`}
                  className="text-xs bg-rose-600/20 hover:bg-rose-600/40 text-rose-300 px-3 py-1.5 rounded-lg flex items-center gap-1">
                  <XCircle size={12} weight="bold" /> Reject
                </button>
              </>
            )}
            <button onClick={onEdit}
              data-testid={`post-edit-${post.id}`}
              className="text-xs bg-stone-700 hover:bg-stone-600 text-stone-200 px-3 py-1.5 rounded-lg flex items-center gap-1">
              <PencilSimple size={12} weight="bold" /> Edit
            </button>
            <button onClick={() => { if (window.confirm("Delete this post permanently?")) onAction(post.id, "delete"); }}
              data-testid={`post-delete-${post.id}`}
              className="text-xs bg-stone-700 hover:bg-rose-600 text-stone-300 hover:text-white px-3 py-1.5 rounded-lg flex items-center gap-1">
              <Trash size={12} weight="bold" /> Delete
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function EditModal({ post, onClose, onSaved }) {
  const [caption, setCaption] = useState(post.caption || "");
  const [trees, setTrees] = useState(post.trees_count || 1);
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/posts/${post.id}/edit`,
        { caption, trees_count: Number(trees) }, { withCredentials: true });
      onSaved();
    } catch (e) { alert(e.response?.data?.detail || "Save failed"); }
    setSaving(false);
  };
  return (
    <div className="fixed inset-0 z-[80] bg-stone-900/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="post-edit-modal">
      <div className="bg-stone-900 border border-stone-700 rounded-2xl max-w-md w-full p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-heading text-base font-semibold text-white">Edit Post</h3>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-stone-500 block mb-1">Caption</label>
          <textarea value={caption} onChange={(e) => setCaption(e.target.value)} rows={3}
            className="w-full bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-emerald-500"
            data-testid="post-edit-caption" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-stone-500 block mb-1">Trees count</label>
          <input type="number" min="1" max="10000" value={trees} onChange={(e) => setTrees(e.target.value)}
            className="w-32 bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-emerald-500"
            data-testid="post-edit-trees" />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="text-xs text-stone-400 px-3 py-1.5">Cancel</button>
          <button onClick={save} disabled={saving}
            data-testid="post-edit-save"
            className="text-xs bg-emerald-600 hover:bg-emerald-500 text-white px-4 py-1.5 rounded-lg">
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

function CertsModeration() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("pending_approval");
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const url = `${API}/admin/certificates?status=${filter}&limit=200`;
      const res = await axios.get(url, { withCredentials: true });
      setItems(res.data.items || []);
    } catch { setItems([]); }
    setLoading(false);
  }, [filter]);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  const act = async (id, verb, body = {}) => {
    try {
      await axios.put(`${API}/admin/certificates/${id}/${verb}`, body, { withCredentials: true });
      fetchAll();
    } catch (e) { alert(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div>
      <div className="flex gap-2 mb-4 flex-wrap">
        {[
          { id: "pending_approval", label: "Pending Approval" },
          { id: "active", label: "Active" },
          { id: "revoked", label: "Revoked" },
        ].map((f) => (
          <button key={f.id} onClick={() => setFilter(f.id)}
            data-testid={`certs-filter-${f.id}`}
            className={`text-xs px-3 py-1 rounded-full border ${
              filter === f.id ? "bg-amber-500 border-amber-600 text-white" : "border-stone-700 text-stone-400"
            }`}>{f.label}</button>
        ))}
      </div>

      {loading ? <p className="text-stone-500 text-sm">Loading…</p> : items.length === 0 ? (
        <p className="text-stone-500 text-sm italic">No certificates in this state.</p>
      ) : (
        <div className="space-y-2">
          {items.map((c) => <CertRow key={c.cert_id} cert={c} onAction={act} />)}
        </div>
      )}
    </div>
  );
}

function CertRow({ cert, onAction }) {
  const [showRevoke, setShowRevoke] = useState(false);
  const [reason, setReason] = useState("");
  return (
    <div className="bg-stone-900 border border-stone-800 rounded-xl p-4" data-testid={`cert-row-${cert.cert_id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-mono text-sm text-emerald-300 mb-0.5">{cert.serial_number}</p>
          <p className="text-xs text-stone-300 truncate">{cert.holder_name} ({cert.buyer_type || "individual"})</p>
          <p className="text-[11px] text-stone-500">{cert.project_name} · Vintage {cert.vintage_year}</p>
        </div>
        <div className="text-right">
          <p className="font-heading text-xl text-white">{Number(cert.tons_co2e).toFixed(2)} <span className="text-xs text-stone-500">tCO₂e</span></p>
          <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${
            cert.status === "pending_approval" ? "bg-amber-500/20 text-amber-300" :
            cert.status === "active" ? "bg-emerald-500/20 text-emerald-300" :
            "bg-rose-500/20 text-rose-300"
          }`}>{cert.status === "pending_approval" ? "Pending" : cert.status}</span>
        </div>
      </div>
      {cert.revoked_reason && (
        <p className="text-xs text-rose-300 bg-rose-500/10 border border-rose-500/20 rounded-lg px-2 py-1 mt-2">
          Revoked: {cert.revoked_reason}
        </p>
      )}
      {showRevoke ? (
        <div className="flex gap-2 mt-3">
          <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason..."
            className="flex-1 px-3 py-1.5 bg-stone-800 border border-stone-700 rounded-lg text-xs text-white focus:outline-none focus:border-rose-500"
            data-testid={`cert-revoke-reason-${cert.cert_id}`} />
          <button onClick={() => { onAction(cert.cert_id, "revoke", { reason }); setShowRevoke(false); }}
            data-testid={`cert-revoke-confirm-${cert.cert_id}`}
            className="text-xs bg-rose-600 hover:bg-rose-500 text-white px-3 py-1.5 rounded-lg">Confirm</button>
          <button onClick={() => setShowRevoke(false)} className="text-xs text-stone-400">Cancel</button>
        </div>
      ) : (
        <div className="flex flex-wrap gap-2 mt-3">
          {cert.status === "pending_approval" && (
            <button onClick={() => onAction(cert.cert_id, "approve")}
              data-testid={`cert-approve-${cert.cert_id}`}
              className="text-xs bg-emerald-600 hover:bg-emerald-500 text-white px-3 py-1.5 rounded-lg flex items-center gap-1">
              <CheckCircle size={12} weight="bold" /> Approve & Activate
            </button>
          )}
          {cert.status !== "revoked" && (
            <button onClick={() => setShowRevoke(true)}
              data-testid={`cert-revoke-${cert.cert_id}`}
              className="text-xs bg-rose-600/20 hover:bg-rose-600/40 text-rose-300 px-3 py-1.5 rounded-lg flex items-center gap-1">
              <Warning size={12} weight="bold" /> Revoke
            </button>
          )}
          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/certificates/${cert.cert_id}/download`}
            target="_blank" rel="noopener noreferrer"
            className="text-xs bg-stone-700 hover:bg-stone-600 text-stone-200 px-3 py-1.5 rounded-lg">
            View PDF
          </a>
        </div>
      )}
    </div>
  );
}
